import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Supported bulk actions (admin-arch §3.0.4 + §3.3 tracks bulk routes).
 * Concrete service-side validation rejects actions not applicable to a
 * given resource — this enum lists every action used anywhere in MVP.
 *
 * Wave 2.17 BE-1 (TASK-042a+b): the v0.1 admin-arch reference is the catalog
 * bulk surface. New runner-table kinds — `tag`, `replace_tag`, `export_csv`,
 * `delete` — sit alongside the original `change_tags` / etc. tokens to keep
 * older callers compiling while the new runner uses the short forms.
 *
 * @deprecated Use `bulkJobKindSchema` (Wave 2.17). The broad union here
 *   carries v0.1 wire tokens (`change_tags`, `change_library`, `change_tier`,
 *   `archive`, `retranscode`) that the new `internal.bulk_jobs.kind` CHECK
 *   constraint rejects. New code that talks to the runner MUST validate
 *   with `bulkJobKindSchema`; this alias stays for older callers and will
 *   be removed once they migrate.
 */
export const bulkActionEnum = z.enum([
    'approve',
    'reject',
    'archive',
    'retire',
    'retranscode',
    'change_tags',
    'change_library',
    'change_tier',
    'delete',
    // Wave 2.17 BE-1 — runner action kinds.
    'tag',
    'replace_tag',
    'export_csv',
]);
/**
 * Narrow alias accepted by the Wave 2.17 bulk runner (`internal.bulk_jobs.kind`
 * CHECK constraint). Distinguished from the broad `bulkActionEnum` so old
 * call sites that still emit `change_tags` etc. do not accidentally land in
 * the new table — the runner only knows how to execute the kinds listed here.
 */
export const bulkJobKindSchema = z.enum([
    'approve',
    'reject',
    'tag',
    'replace_tag',
    'retire',
    'export_csv',
    'delete',
    /**
     * venue-category-discovery D-13 / arch §4.1.6, migration 0119. The one kind
     * whose selection is PRESET ids rather than track ids — the runner's only new
     * branch; the advisory lock, SAVEPOINT-per-chunk, progress counters and the
     * parent-audit-row convention are reused untouched.
     */
    'preset_showcase_apply',
]);
/**
 * 202 Accepted response from any `POST /admin/<resource>/bulk/<action>`
 * (admin-arch §3.0.4 / analyst F-04 + F-05). Final state and per-item
 * results are fetched via `GET /admin/jobs/:job_id`.
 *
 * @deprecated Use `bulkJobResponseSchema` (Wave 2.17). The runner returns
 *   the full job descriptor inline on 202 — the slimmer `{ job_id, total }`
 *   shape is retained only for older callers.
 */
export const bulkAcceptedResponseSchema = z.object({
    job_id: uuidSchema,
    total: z.number().int().nonnegative(),
});
/**
 * Wave 2.17 BE-1 — `internal.bulk_jobs.status` CHECK domain. Distinct from
 * the legacy `bulkJobStatusEnum` (which carries `done`) so a runner row that
 * surfaces in the UI cannot be mistaken for the older async-202 wire format.
 */
export const bulkJobStatusSchema = z.enum([
    'queued',
    'running',
    'completed',
    'failed',
    'cancelled',
]);
/**
 * Legacy status enum kept for backwards-compatible callers of the older
 * `bulkJobSchema` shape. Will be unified once the FE stops reading `done`.
 *
 * @deprecated Use `bulkJobStatusSchema` (Wave 2.17). The runner emits
 *   `completed` rather than the older `done` token; mixing the two is a
 *   wire-contract drift hazard.
 */
export const bulkJobStatusEnum = z.enum([
    'queued',
    'running',
    'done',
    'failed',
    'cancelled',
]);
/**
 * Bulk job descriptor (legacy v0.1 wire shape, kept for older callers /
 * tests). Wave 2.17 BE-1 returns `bulkJobResponseSchema` instead.
 *
 * @deprecated Use `bulkJobResponseSchema` (Wave 2.17). This v0.1 shape
 *   carries `succeeded` / `finished_at` / `resource` fields the runner
 *   does not populate.
 */
export const bulkJobSchema = z.object({
    id: uuidSchema,
    action: bulkActionEnum,
    resource: z.string().min(1).max(64),
    status: bulkJobStatusEnum,
    total: z.number().int().nonnegative(),
    processed: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    created_at: isoDateTimeSchema,
    finished_at: isoDateTimeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Wave 2.17 BE-1 — runner schemas (TASK-042a + 042b).
//
// Each mutation endpoint accepts EITHER an explicit `track_ids` array (max
// 10000 ids) OR a `filter` describing the rows to select. A request that
// supplies both — or neither — fails zod validation; the refine() below
// enforces that exactly one of the two is set.
// ---------------------------------------------------------------------------
/** Upper bound on explicit `track_ids` arrays (defence against pathological
 *  payloads — see admin-arch §3.0.4). */
export const BULK_TRACK_IDS_MAX = 10_000;
/** Domain for the `filter.status` predicate. Mirrors the published track
 *  status set; the bulk runner widens / narrows in the service layer
 *  depending on the action it is executing. */
const bulkFilterStatusEnum = z.enum([
    'uploading',
    'uploaded',
    'transcoding',
    'ready_for_review',
    'published',
    'rejected',
    'archived',
    'requires_dup_review',
    'withdrawn',
]);
/**
 * Bulk filter selector. Translated by the service into a SELECT against
 * `content.tracks` to materialise the target id set inside the same
 * transaction as the mutation. Every field is optional but at least one
 * must be supplied — refined below.
 *
 * R2 review C-2: `account_id` was removed because `content.tracks` does
 * not yet carry the column. Accepting it would either silently drop the
 * predicate (resulting in catalog-wide retire / delete) or require a
 * brittle "fail-on-account_id" branch. It will be reintroduced together
 * with the schema column in a follow-up task.
 */
export const bulkFilterSchema = z
    .object({
    status: bulkFilterStatusEnum.optional(),
    tag_ids: z.array(uuidSchema).min(1).max(200).optional(),
    query: z.string().min(1).max(200).optional(),
    library_type: z.enum(['royalty_free', 'curated']).optional(),
})
    .strict()
    .refine((v) => v.status !== undefined ||
    v.tag_ids !== undefined ||
    v.query !== undefined ||
    v.library_type !== undefined, { message: 'filter must have at least one non-null field' });
/**
 * Selector container — exactly one of `track_ids` / `filter` set.
 * Refined so the service can branch deterministically.
 */
const bulkSelectorShape = {
    track_ids: z
        .array(uuidSchema)
        .min(1)
        .max(BULK_TRACK_IDS_MAX)
        .optional(),
    filter: bulkFilterSchema.optional(),
};
function refineSelector(schema) {
    return schema.refine((v) => (v.track_ids === undefined && v.filter !== undefined) ||
        (v.track_ids !== undefined && v.filter === undefined), {
        message: 'exactly one of `track_ids` or `filter` must be supplied',
    });
}
export const bulkSelectorSchema = refineSelector(z.object(bulkSelectorShape).strict());
// ---- per-action request shapes -------------------------------------------
export const bulkApproveRequestSchema = refineSelector(z.object({ ...bulkSelectorShape }).strict());
export const bulkRejectRequestSchema = refineSelector(z
    .object({
    ...bulkSelectorShape,
    reason: z.string().min(1).max(500),
})
    .strict());
export const bulkTagRequestSchema = refineSelector(z
    .object({
    ...bulkSelectorShape,
    tag_id: uuidSchema,
})
    .strict());
export const bulkReplaceTagRequestSchema = refineSelector(z
    .object({
    ...bulkSelectorShape,
    old_tag_id: uuidSchema,
    new_tag_id: uuidSchema,
})
    .strict()
    .refine((v) => v.old_tag_id !== v.new_tag_id, {
    message: '`old_tag_id` and `new_tag_id` must differ',
}));
export const bulkRetireRequestSchema = refineSelector(z.object({ ...bulkSelectorShape }).strict());
// ---- response shapes -----------------------------------------------------
/**
 * Wave 2.17 BE-1 — single job descriptor returned by
 * `GET /admin/bulk/jobs/:id` and the 202 envelope on mutation endpoints.
 */
export const bulkJobResponseSchema = z.object({
    id: uuidSchema,
    kind: bulkJobKindSchema,
    status: bulkJobStatusSchema,
    target_count: z.number().int().nonnegative(),
    processed_count: z.number().int().nonnegative(),
    failed_count: z.number().int().nonnegative(),
    started_at: isoDateTimeSchema.nullable(),
    completed_at: isoDateTimeSchema.nullable(),
    error_message: z.string().nullable(),
    created_at: isoDateTimeSchema,
});
/**
 * Keyset-paginated list of jobs for the calling admin. Mirrors the audit /
 * track list envelope: `next_cursor` is `null` on the last page; `total` is
 * intentionally not surfaced (job table grows append-only and counting
 * over filters would not pay for itself).
 */
export const bulkJobListResponseSchema = z.object({
    items: z.array(bulkJobResponseSchema),
    next_cursor: z.string().nullable(),
});
/**
 * Query params for the list endpoint. Cursor is the opaque base64 token
 * emitted by the previous page.
 */
export const bulkJobListFilterSchema = z
    .object({
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    status: bulkJobStatusSchema.optional(),
    kind: bulkJobKindSchema.optional(),
})
    .strict();
//# sourceMappingURL=bulk.js.map